import {
  FormControl,
  OutlinedInput,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo } from "react";
import { usePosStore } from "../context/posStore";
import { IProduct, ComponentModal, IGrillingConfig } from "../common/interfaces";
import { v4 } from "uuid";
import { getOptionsSignature } from "../services/pricing";
import { IProductOptionsResult } from "./modal/productOptions";
import lodash, { debounce } from "lodash";
import { productsService } from "../services/productsService";
import { CommonService } from "../services/CommonService";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import { Loader } from "./loader";
import Swal from "sweetalert2";
import "@sweetalert2/theme-dark/dark.css"
import withReactContent from "sweetalert2-react-content";
import { useModalStore } from "../context/modalStore";
const MySwal = withReactContent(Swal);

export const ProductSideBar: React.FC = () => {
  const searchBoxRef = React.useRef<HTMLInputElement>(null);
  const listProducts = usePosStore((state) => state.listProducts)
  const setListProducts = usePosStore((state) => state.setListProducts)
  const products = usePosStore((state) => state.products)
  const setProducts = usePosStore((state) => state.setProducts)
  const reset = usePosStore((state) => state.reset)
  const modalManager = useModalStore()
  const [search, setSearch] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const MIN_QTY = 0.05;

  const handleEnterSearch = (evt: any) => {
    if (evt.key === "Enter") {
      setLoading(true)
      if (/^\d|^\d\*\d+(\.\d{1,5})*?/.test(search)) {
        let [qty, code] = search.split('*');
        if(!qty) qty = '1';
        if(!code) {
          code = qty
          qty = '1'
        };
        
        productsService.getProductByCode(code).then((res) => {
          if (res) {
            const nQty = res.is_bulk === '1' ? parseInt(qty) : Number(qty);

            addToShoppingCart({...res, quantity: Number(nQty) ?? 1});
            setSearch("");
            debounceLoadData('');
            setLoading(false)
            return;
          }
          CommonService.toast("Producto no encontrado", "error");
          setSearch('')
          setLoading(false)
        });
      }
      setLoading(false)
    }
  };


  /** Un producto abre la ventana de opciones si tiene modificadores nuevos u opciones viejas. */
  const hasChoices = (item: IProduct) =>
    (item.option_groups?.length ?? 0) > 0 || (item.options?.length ?? 0) > 0;

  /**
   * Agrega el renglon resultante de la ventana de opciones.
   *
   * Una combinacion identica se suma al renglon que ya existe (ticket y kardex compactos);
   * una combinacion distinta crea un renglon nuevo, que es lo que hace posible que dos
   * parrilladas del mismo producto lleven sabores distintos.
   */
  const addConfiguredProduct = (product: IProduct, results: IProductOptionsResult[]) => {
    // Se acumula sobre un arreglo local y se guarda UNA vez: si se llamara a setProducts
    // dentro del ciclo, cada vuelta leeria el `products` viejo y se perderian renglones.
    const next = [...products];

    results.forEach((result) => {
      const line: IProduct = {
        ...product,
        line_uid: v4(),
        quantity: result.quantity,
        selectedOptions: result.selectedOptions,
        selected_option_items: result.selected_option_items,
      };

      const signature = getOptionsSignature(line);
      const index = next.findIndex(
        (item: IProduct) => !item.is_grilling_service && getOptionsSignature(item) === signature
      );

      if (index === -1) {
        next.push(line);
      } else {
        next[index] = {
          ...next[index],
          quantity: Number(next[index].quantity ?? 1) + Number(result.quantity),
        };
      }
    });

    setProducts(next);
    modalManager.closeModal();
    setSearch('');
    searchBoxRef.current?.focus();
  };

  const openProductOptions = (product: IProduct) => {
    modalManager.openModal(ComponentModal.ProductOptions, {
      product,
      mode: 'add',
      quantity: Number(product.quantity ?? 1),
      onConfirm: (results: IProductOptionsResult[]) => addConfiguredProduct(product, results),
    });
    setSearch('');
  };

  const addToShoppingCart = (product: IProduct) => {
    console.log('🔍 addToShoppingCart - Product:', {
      id: product.id,
      name: product.name,
      product_type_id: product.product_type_id,
      has_grilling_service: product.has_grilling_service,
      is_bulk: product.is_bulk,
      quantity: product.quantity
    });

    // La ventana de opciones va ANTES del chequeo de indice: antes, volver a tocar un
    // producto que ya estaba en el carrito solo subia la cantidad y se saltaba la ventanita,
    // asi que era imposible pedir dos parrilladas con sabores distintos.
    if (hasChoices(product)) {
      openProductOptions(product);
      return;
    }

    // Solo se juntan renglones de productos sin opciones ni configuracion propia.
    const index = products.findIndex(
      (item: IProduct) =>
        item.id === product.id &&
        !item.is_grilling_service &&
        !item.selectedOptions &&
        !item.selected_option_items?.length &&
        !item.grilling_config
    );

    console.log('🔍 Index:', index);

    if (index === -1) {
      // Si es producto a granel, pedir cantidad primero
      if (product.is_bulk === '0'){
        MySwal.fire({
          title: "Cantidad",
          showCancelButton: true,
          input: 'text',
          allowEscapeKey: false,
          allowOutsideClick: false,
          inputValue: "",
          inputAttributes: {
            min: "0",
          },
          preConfirm(inputValue) {
            if(Number.isNaN(Number(inputValue))){
              Swal.showValidationMessage('Solo valores numéricos permitidos')
              return false
            }

            if(Number(inputValue) < MIN_QTY){
              Swal.showValidationMessage('La cantidad mínima son 50 gramos (0.05 kg).');
              return false;
            }

            return inputValue
          },
        }).then((result) => {
          if (result.isConfirmed) {
            const quantity = Number(result.value);

            console.log('🔍 Cantidad confirmada:', quantity);
            console.log('✅ Agregando producto');
            setProducts([...products, { ...product, line_uid: v4(), quantity }]);
            searchBoxRef.current?.focus();
          }
        })
        setSearch('')
        return;
      }

      // Si NO es a granel
      const quantity = Number(product.quantity ?? 1);

      console.log('🔍 Producto NO a granel, cantidad:', quantity);
      console.log('✅ Agregando producto');
      setProducts([...products, { ...product, line_uid: v4(), quantity }]);
      setSearch('');
    } else {
      console.log('⚠️ Producto YA existe en carrito, incrementando cantidad');
      setSearch('')
      const qty = products[index]?.quantity ?? 1;
      products[index].quantity = Number(qty) + Number(product.quantity ?? 1);
      setProducts([...products]);
    }
    searchBoxRef.current?.focus()
  };
  const getProducts = (filter: string) => {
    productsService.getProducts(filter).then((res) => {
      setListProducts(res.data);
      setLoading(false);
    });
  }
  const debounceLoadData = useCallback(debounce(getProducts, 1000), []);
  const doCallbackWithDebounce = useMemo(() => {
    const callback = () => {};
    return lodash.debounce(callback, 1500);
  }, []);
  useEffect(() => {
    setLoading(true);
    getProducts('');
  }, [])
  return (
    <FormControl style={{ width: "100%" }} variant="outlined">
      <OutlinedInput
        id="outlined-adornment-weight"
        placeholder="Busque su producto"
        onChange={(event) => {
          doCallbackWithDebounce();
          setSearch(event.target.value);
          if (!/^\d$|^\d\*$|^\d\*\d+(\.\d{0,5})*$/.test(event.target.value)) {
            debounceLoadData(event.target.value);
          }
        }}
        onKeyUp={handleEnterSearch}
        value={search}
        endAdornment={
          <InputAdornment position="end">
            <img
              className="img-fluid"
              src={`${process.env.PUBLIC_URL}/img/barcode01.png`}
              style={{ width: "50px" }}
              alt="logo"
            />
          </InputAdornment>
        }
        aria-describedby="outlined-weight-helper-text"
        inputRef={searchBoxRef}
        inputProps={{
          "aria-label": "weight",
          autoComplete: "off",
        }}
      /> 
      {/* <button onClick={reset}>Reset</button> */}
      <div style={{ overflow: "auto", height: "750px" }}>
        <List>
          <Loader display={loading} />
          {!loading && listProducts.map((product: any) => (
            <ListItem
              onClick={() => addToShoppingCart(product)}
              key={product.id}
              className="list-item-product"
            >
              <ListItemAvatar>
                <Avatar>
                  <ShoppingBasketIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <span>
                    {product.name}
                    {(product.department_name || product.subcategory_name) && (
                      <span style={{ display: 'block', fontSize: '0.72em', color: '#999', lineHeight: 1.3, marginTop: 1 }}>
                        {[product.department_name, product.subcategory_name].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                }
                secondary={"$ " + (product.price_1 * (1 + Number(product.iva?.porcentaje ?? 0))).toFixed(2)}
              />
            </ListItem>
          ))}
        </List>
      </div>
    </FormControl>
  );
};
